/**
 * GeofenceTab — Read-only map visualization of campus geofence zones.
 *
 * Displays a React-Leaflet MapContainer with Google roadmap tiles.
 * Zone polygons are rendered in color-coded LayerGroups that can be toggled
 * via a LayersControl overlay (top-right).
 *
 * Data shape (geoData — from getgeofence2 → geo1):
 *   Object keyed by zone_type_name, each value is an object whose entries are
 *   either the zone_type_name string or zone objects with:
 *     { zone_name, positions: [[lat,lng],...], layer: number }
 *
 * Props:
 *   geoData   — geo1 object from getgeofence2 (zone data keyed by type)
 *   latitude  — campus center latitude
 *   longitude — campus center longitude
 */

import { useMemo } from 'react';
import { MapContainer, Polygon, Tooltip, LayersControl, LayerGroup } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';

const ZONE_COLORS = ['#1f98ff', '#58c2ff', '#98d3ff', '#259400', '#deb900', '#d02400'];
const GOOGLE_API_KEY = 'AIzaSyDWuEm0U-KbxGBwYlNyc3eiKM1lbWcocis';

const GeofenceTab = ({ geoData, latitude, longitude }) => {
  // Parse lat/lng — default to 0,0 if absent
  const center = useMemo(() => {
    const lat = parseFloat(latitude) || 0;
    const lng = parseFloat(longitude) || 0;
    return [lat, lng];
  }, [latitude, longitude]);

  // Convert geoData object into an array of zone-type groups
  const zoneGroups = useMemo(() => {
    if (!geoData || typeof geoData !== 'object') return [];
    return Object.values(geoData);
  }, [geoData]);

  const hasCenter = center[0] !== 0 || center[1] !== 0;

  // ── Empty state ─────────────────────────────────────────────────────────
  if (!hasCenter && zoneGroups.length === 0) {
    return (
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Geofence</h3>
        </div>
        <div className="pp-section__body">
          <div className="cpp-int-empty">
            <svg className="cpp-int-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
            </svg>
            <p className="cpp-int-empty__title">No geofence data available</p>
            <p className="cpp-int-empty__subtitle">Zones can be created using the Zone Designer tab.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Map ──────────────────────────────────────────────────────────────────
  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">Geofence</h3>
      </div>
      <div className="pp-section__body" style={{ padding: 0 }}>
        <div className="cpp-map-container">
          <MapContainer
            className="rounded"
            zoom={19}
            scrollWheelZoom={true}
            center={center}
            style={{ height: '100%', width: '100%' }}
          >
            <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="roadmap" />
            <LayersControl position="topright">
              {zoneGroups.map((group) => {
                const zones = Object.values(group);
                const typeName = group?.zone_type_name || 'Unknown';

                return (
                  <LayersControl.Overlay key={typeName} checked name={typeName}>
                    <LayerGroup>
                      {zones.map((zone, idx) => {
                        // Skip the zone_type_name string entry
                        if (typeof zone === 'string') return null;
                        if (!zone?.positions) return null;

                        return (
                          <Polygon
                            key={zone.zone_name || idx}
                            positions={zone.positions}
                            pathOptions={{ color: ZONE_COLORS[zone.layer] || ZONE_COLORS[0] }}
                          >
                            <Tooltip>{zone.zone_name}</Tooltip>
                          </Polygon>
                        );
                      })}
                    </LayerGroup>
                  </LayersControl.Overlay>
                );
              })}
            </LayersControl>
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default GeofenceTab;
